import {
  queryOptions,
  type Query,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";

import {
  sessionSchema,
  type Session,
} from "../../../entities/session/session";
import { resolveApiBaseUrl } from "../../../shared/api/client";
import { adminQueryKeys } from "../../admin/api/query-keys";

export const anonymousSession = {
  authenticated: false,
  user: null,
  expires_at: null,
} as const satisfies Session;

export const memberQueryKeys = {
  all: ["member"] as const,
  session: ["member", "session"] as const,
  users: ["member", "user"] as const,
  forUser: <Scope extends QueryKey>(
    userId: string,
    ...scope: Scope
  ) => ["member", "user", userId, ...scope] as const,
};

function isSessionKey(queryKey: readonly unknown[]): boolean {
  return (
    queryKey.length === memberQueryKeys.session.length &&
    queryKey.every(
      (part, index) => part === memberQueryKeys.session[index],
    )
  );
}

function isPrivateMemberQuery(query: Query): boolean {
  return query.queryKey[0] === "member" && !isSessionKey(query.queryKey);
}

export async function establishMemberSession(
  queryClient: QueryClient,
  session: Session,
): Promise<void> {
  if (!session.authenticated) {
    throw new SessionRequestError(
      "Uma sessão autenticada é necessária para estabelecer a conta.",
    );
  }
  await queryClient.cancelQueries({
    queryKey: memberQueryKeys.all,
  });
  await purgeAdminIdentity(queryClient);
  queryClient.removeQueries({
    predicate: isPrivateMemberQuery,
  });
  queryClient.setQueryData(memberQueryKeys.session, session);
}

export async function purgeAdminIdentity(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries({
    queryKey: adminQueryKeys.all,
  });
  queryClient.removeQueries({
    queryKey: adminQueryKeys.all,
  });
}

export async function purgeMemberIdentity(
  queryClient: QueryClient,
  userId: string,
): Promise<void> {
  const predicate = (query: Query) => {
    if (!isPrivateMemberQuery(query)) {
      return false;
    }
    const [, namespace, owner] = query.queryKey;
    return namespace !== "user" || owner === userId;
  };
  await queryClient.cancelQueries({ predicate });
  queryClient.removeQueries({ predicate });
}

export class SessionRequestError extends Error {
  readonly status: number | null;

  constructor(
    message: string,
    options: {
      readonly cause?: unknown;
      readonly status?: number;
    } = {},
  ) {
    super(message, options.cause === undefined ? {} : { cause: options.cause });
    this.name = "SessionRequestError";
    this.status = options.status ?? null;
  }
}

export class SessionExpiredError extends SessionRequestError {
  constructor() {
    super("A sessão autenticada expirou.", { status: 200 });
    this.name = "SessionExpiredError";
  }
}

function sessionUrl(): string {
  return `${resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL).replace(/\/$/u, "")}/auth/session`;
}

export async function fetchSession(
  fetcher: typeof fetch = globalThis.fetch,
): Promise<Session> {
  let response: Response;
  try {
    response = await fetcher(sessionUrl(), {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
    });
  } catch (cause) {
    throw new SessionRequestError(
      "Não foi possível consultar a sessão.",
      { cause },
    );
  }

  if (response.status === 401) {
    return anonymousSession;
  }
  if (response.status !== 200) {
    throw new SessionRequestError(
      "A consulta de sessão retornou um estado inesperado.",
      { status: response.status },
    );
  }
  if (
    !response.headers
      .get("content-type")
      ?.toLowerCase()
      .includes("application/json")
  ) {
    throw new SessionRequestError(
      "A consulta de sessão retornou um formato inesperado.",
      { status: response.status },
    );
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch (cause) {
    throw new SessionRequestError(
      "A resposta da sessão não contém JSON válido.",
      { cause, status: response.status },
    );
  }

  const parsed = sessionSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SessionRequestError(
      "A resposta da sessão não corresponde ao contrato.",
      { cause: parsed.error, status: response.status },
    );
  }
  if (
    parsed.data.authenticated &&
    Date.parse(parsed.data.expires_at) <= Date.now()
  ) {
    throw new SessionExpiredError();
  }

  return parsed.data;
}

export function sessionQueryOptions() {
  return queryOptions<
    Session,
    SessionRequestError,
    Session,
    typeof memberQueryKeys.session
  >({
    queryKey: memberQueryKeys.session,
    queryFn: () => fetchSession(),
    retry: false,
    staleTime: 30_000,
  });
}
